/**
 * useHrAbsenceTypeSetting — Ustawienia → „Typy dokumentów": ustawienie klienta „który typ
 * dokumentu jest wnioskiem o nieobecność" (`GET/PUT /api/settings/hr-absence-type`) jako kolumna
 * tabeli typów (zaznaczenie w wierszu), a nie osobna sekcja.
 *
 * Model: dokładnie jeden typ jest „tak" albo żaden (`typeId: null`). Zaznaczenie wiersza przenosi
 * „tak" na ten typ (PUT `{ typeId: id }`), odznaczenie aktywnego wiersza zdejmuje je (PUT
 * `{ typeId: null }`). Typ globalny jest w tabeli rozwinięty na wiersz per firma pod tym samym `id`,
 * więc jest zaznaczony we wszystkich swoich wierszach naraz — ustawienie dotyczy całego klienta.
 *
 * Zapytanie jest włączane tylko, gdy kolumna w ogóle jest widoczna (`enabled`). Błąd odczytu nie
 * pokazuje bannera — komórki dostają `isLoaded === false` i rysują „—". Po zapisie czekamy na
 * świeży stan ustawienia (mutacja jest „w toku" aż do odświeżenia), a równolegle odświeżamy menu
 * (`/my/installed-modules`, skrót „Nieobecności") i pulpit Kadr (`/hr/metrics`).
 */

import { useTranslation } from 'react-i18next';
import type {
  HrAbsenceTypeSettingResponse,
  HrAbsenceTypeSettingUpdate,
  HrAbsenceTypeSettingUpdateResponse,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  DOCUMENT_TYPES_QUERY_KEY,
  HR_ABSENCE_TYPE_DEPENDENT_QUERY_KEYS,
  HR_ABSENCE_TYPE_SETTING_QUERY_KEY,
} from '@/lib/hr/query-keys';

/** Kody błędów zapisu z czytelnym komunikatem. */
type SaveErrorCode =
  | 'HR_ABSENCE_TYPE_NOT_FOUND'
  | 'HR_ABSENCE_TYPE_DELETED'
  | 'HR_ABSENCE_TYPE_NOT_PUBLISHED'
  | 'HR_ABSENCE_TYPE_CLASS_NOT_ALLOWED'
  | 'GLOBAL_TYPES_UNAVAILABLE'
  | 'PERMISSION_DENIED';

/** Kod błędu zapisu → klucz komunikatu (toast). Kod spoza mapy → ogólny błąd zapisu. */
const SAVE_ERROR_KEYS: Record<SaveErrorCode, string> = {
  HR_ABSENCE_TYPE_NOT_FOUND: 'tenant.documentTypes.hrAbsenceType.toast.notFound',
  HR_ABSENCE_TYPE_DELETED: 'tenant.documentTypes.hrAbsenceType.toast.deleted',
  HR_ABSENCE_TYPE_NOT_PUBLISHED: 'tenant.documentTypes.hrAbsenceType.toast.notPublished',
  HR_ABSENCE_TYPE_CLASS_NOT_ALLOWED: 'tenant.documentTypes.hrAbsenceType.toast.classNotAllowed',
  GLOBAL_TYPES_UNAVAILABLE: 'tenant.documentTypes.hrAbsenceType.toast.catalogUnavailable',
  PERMISSION_DENIED: 'tenant.documentTypes.hrAbsenceType.toast.permissionDenied',
};

/** Błędy oznaczające, że wskazany typ jest nieaktualny — odświeżamy ustawienie i tabelę typów. */
const STALE_TYPE_ERROR_CODES: ReadonlySet<SaveErrorCode> = new Set<SaveErrorCode>([
  'HR_ABSENCE_TYPE_NOT_FOUND',
  'HR_ABSENCE_TYPE_DELETED',
  'HR_ABSENCE_TYPE_NOT_PUBLISHED',
  'HR_ABSENCE_TYPE_CLASS_NOT_ALLOWED',
]);

function isSaveErrorCode(code: string): code is SaveErrorCode {
  return Object.hasOwn(SAVE_ERROR_KEYS, code);
}

/** Kod błędu z odpowiedzi API (`{ error: string }`) albo `null`. */
function apiErrorCode(err: unknown): string | null {
  if (!isAxiosError(err)) return null;
  const data: unknown = err.response?.data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const raw: unknown = data.error;
  return typeof raw === 'string' ? raw : null;
}

/** Argumenty mutacji: docelowy typ (`null` = zdjęcie oznaczenia) i nazwa do komunikatu sukcesu. */
interface ToggleVariables {
  typeId: string | null;
  name: string;
}

export interface HrAbsenceTypeSettingState {
  /** Czy stan ustawienia jest znany (zapytanie się powiodło). Gdy `false` — komórki rysują „—". */
  isLoaded: boolean;
  /** Id typu będącego wnioskiem o nieobecność; `null` = żaden (lub stan nieznany). */
  typeId: string | null;
  /** Czy osoba może zmieniać ustawienie (`canManage` z serwera); przed odczytem `false`. */
  canManage: boolean;
  /** Zapis w toku — kolumna blokuje wszystkie pola wyboru. */
  isPending: boolean;
  /** Ustawia (`checked = true`) albo zdejmuje (`false`) oznaczenie dla danego typu. */
  setAbsenceType: (typeId: string, name: string, checked: boolean) => void;
}

export function useHrAbsenceTypeSetting(enabled: boolean): HrAbsenceTypeSettingState {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: HR_ABSENCE_TYPE_SETTING_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<HrAbsenceTypeSettingResponse>('/settings/hr-absence-type');
      return res.data;
    },
    enabled,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async ({ typeId }: ToggleVariables) => {
      const body: HrAbsenceTypeSettingUpdate = { typeId };
      const res = await api.put<HrAbsenceTypeSettingUpdateResponse>(
        '/settings/hr-absence-type',
        body,
      );
      return res.data;
    },
    onSuccess: async (_data, { typeId, name }) => {
      for (const queryKey of HR_ABSENCE_TYPE_DEPENDENT_QUERY_KEYS) {
        if (queryKey !== HR_ABSENCE_TYPE_SETTING_QUERY_KEY) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
      toast.show({
        variant: 'success',
        title:
          typeId === null
            ? t('tenant.documentTypes.hrAbsenceType.toast.cleared')
            : t('tenant.documentTypes.hrAbsenceType.toast.set', { name }),
      });
      // Czekamy na świeży stan — inaczej pole na chwilę wróciłoby do starej wartości.
      await queryClient.invalidateQueries({ queryKey: HR_ABSENCE_TYPE_SETTING_QUERY_KEY });
    },
    onError: async (err) => {
      const raw = apiErrorCode(err);
      const code = raw !== null && isSaveErrorCode(raw) ? raw : null;
      const stale = code !== null && STALE_TYPE_ERROR_CODES.has(code);
      toast.show({
        variant: code !== null ? 'warning' : 'error',
        title: t(
          code !== null
            ? SAVE_ERROR_KEYS[code]
            : 'tenant.documentTypes.hrAbsenceType.toast.saveError',
        ),
        ...(stale
          ? { description: t('tenant.documentTypes.hrAbsenceType.toast.listRefreshed') }
          : {}),
      });
      if (stale) {
        // Toast obiecuje odświeżoną listę — odświeżamy i ustawienie, i tabelę typów (wiersz typu
        // usuniętego / wycofanego ma zniknąć, nie zostać klikalny).
        void queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: HR_ABSENCE_TYPE_SETTING_QUERY_KEY });
      }
    },
  });

  // Nieudane odświeżenie (np. po zapisie) zostawia w cache stare dane — nie pokazujemy ich jako
  // aktualnych, tylko „—".
  const setting = query.isError ? undefined : query.data;

  return {
    isLoaded: setting !== undefined,
    typeId: setting?.typeId ?? null,
    canManage: setting?.canManage ?? false,
    isPending: mutation.isPending,
    setAbsenceType: (typeId, name, checked) =>
      mutation.mutate({ typeId: checked ? typeId : null, name }),
  };
}
